import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classes from '@/components/Collections/Collection/Collection.module.scss';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { Button } from '@/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Collection from '@/utils/api/odas/collection';
import { toastContext } from '@/context/toast.context';
import { PERMISSION_TYPES } from '@/hooks/useCollectionPermissions';
import { isSuccessResponse } from '@/constants/httpStatus';
import isEmpty from 'lodash/isEmpty';
import EmailBadgeInput from '@/components/EmailBadgeInput/EmailBadgeInput';
import t from '@/utils/Language/Collection-translations';

const DEFAULT_PERMISSION_TYPE = PERMISSION_TYPES.VIEWER;

const InviteCollaboratorsCard = ({ collection, updateCollection }) => {
  const { addToast } = useContext(toastContext);
  const [selectedPermissionType, setSelectedPermissionType] = useState(DEFAULT_PERMISSION_TYPE);
  const [emails, setEmails] = useState([]);
  const [resetKey, setResetKey] = useState(0);

  const permissionTypeList = useMemo(
    () => [
      { title: t('Owner (edit and manage users)'), value: PERMISSION_TYPES.OWNER },
      { title: t('Editor (add and remove resources)'), value: PERMISSION_TYPES.EDITOR },
      { title: t('Viewer (view and share this collection)'), value: PERMISSION_TYPES.VIEWER }
    ],
    [t]
  );

  const handlePermissionTypeChange = (e) => {
    setSelectedPermissionType(parseInt(e.target.value, 10));
  };

  const resetForm = () => {
    setSelectedPermissionType(DEFAULT_PERMISSION_TYPE);
    setEmails([]);
    setResetKey((k) => k + 1);
  };

  const sendInvites = (shouldSendEmail = true) => {
    const emailList = emails.join(',');

    if (isEmpty(emailList)) {
      addToast({
        message: t('Please enter at least one email address.'),
        type: 'warning'
      });
      return;
    }

    Collection.inviteCollaborators(collection.id, emailList, selectedPermissionType, !shouldSendEmail)
      .then((resp) => {
        if (isSuccessResponse(resp.status)) {
          updateCollection();
          resetForm();
          addToast({
            message: t('Permission has been changed.'),
            type: 'success'
          });
        } else {
          addToast({ type: 'error' });
        }
      })
      .catch(() => {
        addToast({ type: 'error' });
      });
  };

  const handleAddWithoutInvite = () => sendInvites(false);

  const handleAddAndSendInvite = () => sendInvites(true);

  const renderPermissionTypeSelect = () => (
    <div className="mb-3">
      <label className={classes.boldLabel}>
        {t('Collaborator type')}
        <span className={classes.requiredLabel}>*</span>
      </label>
      <Form.Group className="mb-3" controlId="formBasicPermissionType">
        <div className={classes.selectWrapper}>
          <Form.Select className={classes.selectDropdown} onChange={handlePermissionTypeChange} value={selectedPermissionType} name="typeToInvite">
            {permissionTypeList.map((type) => (
              <option key={type.value} value={type.value}>
                {type.title}
              </option>
            ))}
          </Form.Select>
          <FontAwesomeIcon icon={['fas', 'chevron-down']} className={classes.selectIcon} />
        </div>
      </Form.Group>
    </div>
  );

  const renderEmailInput = () => (
    <div>
      <label className={classes.boldLabel}>
        {t('Collaborator emails')}
        <span className={classes.requiredLabel}>*</span>
      </label>
      <Form.Group controlId="formBasicName">
        <EmailBadgeInput
          key={resetKey} // reserved prop `key` lets react reset state after emails are submitted
          placeholder={t('Enter email addresses...')}
          onChange={setEmails}
        />
      </Form.Group>
    </div>
  );

  const renderActionButtons = () => (
    <div className={classes.editInvitationBtn}>
      <Button
        dataCy="modal-collection-button-add-without-invite"
        type="link"
        onButtonClick={handleAddWithoutInvite}
        additionalClasses={`${classes.actionButton} ${classes.addWithoutInviteBtn}`}
        text={t('Add without sending invites')}
      />
      <Button
        dataCy="modal-collection-button-add-send-invite"
        type="primary"
        icon={{ style: 'fas', font: 'check' }}
        onButtonClick={handleAddAndSendInvite}
        text={t('Add & send invite')}
        additionalClasses={`${classes.actionButton} ${classes.addSendInviteBtn}`}
      />
    </div>
  );

  /* MAIN RENDER */
  return (
    <Card className={classes.inviteCollaboratorsInformation}>
      <Card.Body className={classes.permissionDetails}>
        <Row className={classes.infoCardRow}>
          <Col lg="3" className={classes.infoCardCol}>
            <div className={classes.permissionEditLabel}>
              <FontAwesomeIcon icon={['fas', 'user']} />
              &nbsp;&nbsp; {t('Invite collaborators')}
            </div>
          </Col>
          <Col lg="1" className={`${classes.divider} ${classes.infoCardCol}`}></Col>
          <Col lg="8" className={classes.infoCardCol}>
            <div className="panel bottom-pane grey">
              <div className={`form-group mb-3 ${classes.formVerticallyConnected}`}>
                {renderPermissionTypeSelect()}
                {renderEmailInput()}
              </div>
            </div>
          </Col>
        </Row>
        {renderActionButtons()}
      </Card.Body>
    </Card>
  );
};

InviteCollaboratorsCard.propTypes = {
  collection: PropTypes.shape({
    id: PropTypes.number.isRequired
  }).isRequired,
  updateCollection: PropTypes.func.isRequired
};

export default InviteCollaboratorsCard;
